/* eslint-disable lingui/no-unlocalized-strings -- Approved First Round plan copy. */
import type { FirstRoundMe } from "@/client/services/pricing/first-round";
import type { DialogType, UpgradeInfo } from "@/client/stores/payment";

const PLAN_NAMES: Record<string, string> = { FREE: "Free", BASIC: "Starter", PRO: "Active" };

const planName = (me: FirstRoundMe | null | undefined, info?: UpgradeInfo) =>
  me?.pass?.planName ?? PLAN_NAMES[info?.tier ?? me?.tier ?? "FREE"] ?? "your plan";

const formatDay = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });

export type PlanDialogHeading = { title: string; reason: string | null };

/** The dialog's title and the line under it: why it opened, in the user's terms. */
export const planDialogHeading = (
  type: DialogType,
  info: UpgradeInfo | undefined,
  me: FirstRoundMe | null | undefined,
): PlanDialogHeading => {
  const plan = planName(me, info);
  const limit = info?.limit;

  switch (type) {
    case "CHOOSE_PLAN": {
      return {
        title: "Choose your plan",
        reason:
          "Start free, or pick a pass for full AI practice interviews. You can upgrade any time.",
      };
    }
    case "DEMO_USED_UP": {
      const tries = me?.demo.limit ?? limit;
      return {
        title: "Ready for a full practice interview?",
        reason: `You've used your ${tries ? `${tries} ` : ""}demo ${tries === 1 ? "try" : "tries"}. Starter and Active give you full AI-coached practice interviews for your own jobs.`,
      };
    }
    case "INTERVIEW_PRACTICE": {
      if (me?.pass) {
        return {
          title: "You've used your practice interviews",
          reason: `Your ${me.pass.planName} pass included ${me.practice.limit ?? limit ?? "its"} practice interviews.${
            me.upgrade ? ` Upgrade to ${me.upgrade.toPlanName} for more.` : ""
          }`,
        };
      }
      return {
        title: "Practise with a full AI interview",
        reason:
          "Full practice interviews come with Starter and Active. Free includes the 2-minute demo.",
      };
    }
    case "PASS_ENDED": {
      const last = me?.lastPass;
      return {
        title: "Your pass has ended",
        reason: last
          ? `Your ${last.planName} pass ended on ${formatDay(last.endedAt)}. You're on Free now; your resumes are still here.`
          : "You're on Free now; your resumes are still here.",
      };
    }
    case "CREATE":
    case "RESUME_CREATION": {
      return {
        title: "You've reached your resume limit",
        reason: `${plan} includes ${limit ?? me?.resumes.limit ?? "a set number of"} resumes. Your resumes stay; a bigger plan lets you add more.`,
      };
    }
    case "DOWNLOAD":
    case "PDF_DOWNLOAD": {
      return {
        title: "You've used this month's downloads",
        reason: `${plan} includes ${limit ?? "a set number of"} PDF downloads a month.`,
      };
    }
    case "AI_TAILORING": {
      return {
        title: "You've used this month's tailoring",
        reason: `${plan} includes ${limit ?? "a set number of"} AI tailorings a month.`,
      };
    }
    case "PLANS":
    case "SUBSCRIPTION_LAPSED": {
      return { title: "Plans", reason: null };
    }
    default: {
      return {
        title: "You've reached a limit",
        reason: info?.featureName
          ? `You've reached the ${info.featureName} limit on ${plan}.`
          : `You've reached a limit on ${plan}.`,
      };
    }
  }
};
